import type { MbCoverageTable } from "#shared/schemas/mbEnrich.ts";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { cn } from "@web/lib/utils.ts";
import { useMbCoverage } from "./mbApi.ts";

const TABLE_TITLES: Record<MbCoverageTable["entity_kind"], string> = {
  artist: "Artists",
  recording: "Recordings",
  release: "Releases",
};

function CoverageTableBlock({ table }: { table: MbCoverageTable }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold tracking-wide text-text uppercase">
        {TABLE_TITLES[table.entity_kind]} — {table.enriched.toLocaleString()} enriched /{" "}
        {table.total.toLocaleString()} total
      </h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs tracking-wider text-faint uppercase">
            <th className="py-1.5 pr-3 font-semibold">Field</th>
            <th className="py-1.5 pr-3 font-semibold tabular-nums">Filled</th>
            <th className="py-1.5 font-semibold tabular-nums">Coverage</th>
          </tr>
        </thead>
        <tbody>
          {table.fields.map((row) => (
            <tr key={row.label} className="border-b border-line/60">
              <td className="py-1.5 pr-3 text-muted">{row.label}</td>
              <td className="py-1.5 pr-3 tabular-nums text-text">{row.filled.toLocaleString()}</td>
              <td className="py-1.5 tabular-nums text-text">
                {row.of === 0 ? "—" : `${row.pct}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {table.rel_types && table.rel_types.length > 0 && (
        <details className="pt-1">
          <summary className="cursor-pointer text-xs text-faint hover:text-muted">
            Artist relation types ({table.rel_types.length})
          </summary>
          <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto pl-1 text-xs text-muted">
            {table.rel_types.map((r) => (
              <li key={r.type} className="flex justify-between gap-3">
                <span>{r.type}</span>
                <span className="tabular-nums">{r.filled.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Field-level coverage from stored raw_json + overall MBID progress. */
export function CoverageDashboard({
  playlistId,
  onPlaylistChange,
  playlists,
}: {
  playlistId: string | null;
  onPlaylistChange: (id: string | null) => void;
  playlists: { id: string; name: string }[];
}) {
  const q = useMbCoverage(playlistId);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-text">Coverage</h2>
          <p className="mt-1 text-sm text-muted">
            Field fill rates among enriched entities (from stored JSON). Overall progress
            is how many library rows have an MBID at all.
          </p>
        </div>
        <label className="space-y-1">
          <span className="text-xs text-muted">Playlist filter</span>
          <select
            value={playlistId ?? ""}
            onChange={(e) => onPlaylistChange(e.target.value || null)}
            className="block min-w-[14rem] rounded-md border border-line bg-panel-elevated px-3 py-2 text-sm text-text"
          >
            <option value="">Entire library</option>
            {playlists.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : q.isError || !q.data ? (
        <p className="text-sm text-status-cancelled">
          {q.error instanceof Error ? q.error.message : "Could not load coverage"}
        </p>
      ) : (
        <div className="space-y-6 rounded-lg border border-line bg-panel px-4 py-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">
              Enrichment progress
              {q.data.playlist_name ? ` · ${q.data.playlist_name}` : ""}
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {q.data.progress.map((row) => (
                <div
                  key={row.entity_kind}
                  className="rounded-md border border-line bg-panel-elevated px-3 py-2"
                >
                  <p className="text-xs capitalize text-muted">{row.entity_kind}s</p>
                  <p className="mt-0.5 text-sm tabular-nums text-text">
                    {row.with_mbid.toLocaleString()} / {row.total.toLocaleString()}
                    <span className={cn("ml-2 text-muted", row.total === 0 && "opacity-50")}>
                      ({row.pct}%)
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </div>

          {q.data.tables.map((table) => (
            <CoverageTableBlock key={table.entity_kind} table={table} />
          ))}
        </div>
      )}
    </section>
  );
}
